import { Col, Container, Row } from 'react-bootstrap'
import React, { useRef, useState } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAccountUserSettingsQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { useGetMeQuery, useSaveUserByIdMutation } from '../../../store/user/user'
import { TimezoneSelector } from '../../../components/TimezoneSelector/TimezoneSelector'
import { CurrentTimeDisplay } from '../../../components/TimezoneSelector/CurrentTimeDisplay'
import { DEFAULT_TIMEZONE } from '../../../components/TimezoneSelector/constants'

export const UserTimezoneSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectAuthenticatedUser)
  const userId = currentUser?.id

  const [showChangesSaved, setShowChangesSaved] = useState<boolean>(false)
  const changesSavedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data: accountUserSettings, isFetching: accountUserSettingsLoading } = useGetAccountUserSettingsQuery(
    { accountId },
    { refetchOnMountOrArgChange: true },
  )
  const { data: userData, isFetching: userLoading } = useGetMeQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const [saveUserById, { isLoading: userSaveLoading }] = useSaveUserByIdMutation()

  const isLoading = accountUserSettingsLoading || userLoading

  const accountDefaultTimezone = accountUserSettings?.data.settingsOverride.timezone ?? DEFAULT_TIMEZONE

  const userTimezoneOverride = userData?.settings?.timezone ?? null
  const effectiveUserTimezone = userData?.effectiveSettings?.timezone ?? DEFAULT_TIMEZONE

  const handleTimezoneSelected = async (selectedTimezone: string | null) => {
    if (!userId) {
      return
    }
    await saveUserById({
      accountId,
      userId,
      keepEmptyItems: true,
      userData: {
        settings: {
          timezone: selectedTimezone,
        },
      },
    }).unwrap()

    setShowChangesSaved(true)
    if (changesSavedTimeoutRef.current) {
      clearTimeout(changesSavedTimeoutRef.current)
    }
    changesSavedTimeoutRef.current = setTimeout(() => {
      setShowChangesSaved(false)
    }, 2000)
  }

  return (
    <Container className={'d-flex flex-column my-5 p-0'}>
      <Row className='mb-4'>
        <Col>
          <h4 className='zocks-text-gray-900 mb-2'>Time zone</h4>
        </Col>
      </Row>
      <Row className='mb-4'>
        <Col>
          <p className='zocks-text-gray-900 p-0 m-0'>
            Zocks uses this time zone when scheduling meetings, processing timestamps, and interpreting time references.
          </p>
        </Col>
      </Row>
      <Row>
        <div className='label-small mb-1'>Your time zone</div>
      </Row>
      <Row className='mb-4'>
        <Col className='pe-2'>
          <TimezoneSelector
            initialSelectedTimezone={userTimezoneOverride}
            accountDefaultTimezone={accountDefaultTimezone}
            onTimezoneSelected={handleTimezoneSelected}
            loading={!userTimezoneOverride && !accountDefaultTimezone}
            disabled={isLoading || userSaveLoading}
          />
        </Col>
      </Row>

      <Row className='mb-4'>
        <Col>
          <div className='d-flex align-items-center justify-content-between gap-3'>
            {effectiveUserTimezone && <CurrentTimeDisplay timezone={effectiveUserTimezone} />}
            {showChangesSaved ? (
              <div className='label-small text-success d-flex gap-1 align-items-center'>
                <i className='bi bi-check fs-6'></i>
                <span>Changes saved</span>
              </div>
            ) : null}
          </div>
        </Col>
      </Row>
    </Container>
  )
}
